import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { FirebaseAuthService } from '../auth/firebase/firebase-auth.service';
import { PrizeEngineService } from '../http/prize-engine.service';
import { CONSUME_DISPLAY_BONUS } from '../http/base-http.service';
import { POPUP_NOTIFICATION_REGISTRY, PopupNotificationConfig } from './custom-notification.registry';
import { CustomNotificationContentService } from './custom-notification-content.service';
import { NotificationContent } from './custom-notification.content';

interface CustomNotificationDoc {
  id?: string;
  notificationId: string;
  notificationType?: string;
  isShowed?: boolean;
}

function toRoute(url?: string): string[] | undefined {
  return url ? ['', ...url.split('/')] : undefined;
}

@Injectable({
  providedIn: 'root'
})
export class CustomNotificationService {

  private readonly handled = new Set<string>();

  constructor(
    private firebaseAuth: FirebaseAuthService,
    private afs: AngularFirestore,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private prizeEngine: PrizeEngineService,
    private content: CustomNotificationContentService,
  ) {}

  public watchNotifications(): Observable<CustomNotificationDoc> {
    return this.firebaseAuth.getAuthState().pipe(
      filter(user => !!user?.uid),
      distinctUntilChanged((a, b) => a?.uid === b?.uid),
      switchMap(user =>
        this.afs.collection<CustomNotificationDoc>(
          `backend-data-users/${user.uid}/custom-notifications`,
          ref => ref.where('isShowed', '==', false)
        ).valueChanges({ idField: 'id' })
      ),
      map(docs => this.selectNext(docs)),
      filter(next => !!next),
      tap(next => this.show(next)),
      concatMap(next => this.consume(next)),
    );
  }

  private selectNext(docs: CustomNotificationDoc[]): CustomNotificationDoc {
    return docs.find(doc =>
      !!POPUP_NOTIFICATION_REGISTRY[doc.notificationId] && !this.handled.has(doc.id)
    );
  }

  private show(next: CustomNotificationDoc): void {
    const config = POPUP_NOTIFICATION_REGISTRY[next.notificationId];
    this.handled.add(next.id);

    this.content.resolve(next.notificationId).subscribe(content => {
      try {
        this.present(config, content);
      } catch {
        this.handled.delete(next.id);
      }
    });
  }

  private present(config: PopupNotificationConfig, content: NotificationContent): void {
    if (config.kind === 'modal') {
      this.dialog.open(config.component, {
        backdropClass: 'blur-backdrop',
        panelClass: config.panelClass,
        maxWidth: '95vw',
        data: {
          ...(config.data as Record<string, unknown> ?? {}),
          title: content.title,
          body: content.msg,
          bodyHtml: content.msgHtml,
          ctaText: content.ctaText,
          heroSrc: content.assetUrl,
          ctaRoute: toRoute(content.url) ?? (config.data as any)?.ctaRoute,
        },
      });
    } else if (config.kind === 'toast-component') {
      this.snackBar.openFromComponent(config.component, {
        data: { ...config.data, title: content.title, body: content.msg, bodyHtml: content.msgHtml, ctaText: content.ctaText, icon: content.assetUrl },
        duration: config.duration,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: config.panelClass,
      });
    } else {
      this.snackBar.open(content.msg || config.text, '', {
        duration: config.duration,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: config.panelClass,
      });
    }
  }

  private consume(next: CustomNotificationDoc): Observable<CustomNotificationDoc> {
    return this.prizeEngine.post(CONSUME_DISPLAY_BONUS, { notificationId: next.notificationId }).pipe(
      take(1),
      catchError(() => of(null)),
      map(() => next),
    );
  }
}
